import { useEffect } from 'react';

/**
 * Hook to handle Escape key press for an individual modal/dialog.
 */
export function useEscapeKey(onClose?: () => void, isActive: boolean = true) {
  useEffect(() => {
    if (!isActive || !onClose) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc' || e.code === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onClose, isActive]);
}

/**
 * Global keyboard listener to close the topmost open modal or pop-up when the Escape key is pressed.
 * Compatible with all modals across the platform (.modal-backdrop, .cal-modal-backdrop, .submit-modal-backdrop, .notif-backdrop, .profile-backdrop, [role="dialog"], overlays).
 */
export function useGlobalModalEscape(onEscapeFallback?: () => void) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Esc' && e.code !== 'Escape') {
        return;
      }

      // Query all open modal overlays/backdrops or dialog elements in the document
      const backdrops = Array.from(
        document.querySelectorAll<HTMLElement>(
          '.modal-backdrop, .cal-modal-backdrop, .submit-modal-backdrop, .notif-backdrop, .profile-backdrop, [role="dialog"], .dialog-overlay, .popup-overlay, [class*="modal-backdrop"], [class*="-backdrop"], .notif-dropdown, .profile-dropdown-card'
        )
      ).filter((el) => {
        if (!el.isConnected) return false;
        const style = window.getComputedStyle(el);
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0)
        );
      });

      if (backdrops.length === 0) {
        if (onEscapeFallback) {
          onEscapeFallback();
        }
        return;
      }

      // Sort modals to pick the topmost one (highest computed z-index, or latest in DOM tree)
      const sorted = [...backdrops].sort((a, b) => {
        const zA = parseInt(window.getComputedStyle(a).zIndex, 10) || 0;
        const zB = parseInt(window.getComputedStyle(b).zIndex, 10) || 0;
        if (zA !== zB) return zA - zB;
        return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      });

      const topModal = sorted[sorted.length - 1];
      if (!topModal) {
        if (onEscapeFallback) onEscapeFallback();
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Priority 1: Direct modal close button
      const closeBtn = topModal.querySelector<HTMLElement>(
        '.modal-close-btn, .cal-modal-close-btn, .submit-modal-close, [data-modal-close], button[aria-label*="Close" i], button[title*="Close" i], button[title*="Kapat" i], .close-btn, .btn-close'
      );
      if (closeBtn) {
        closeBtn.click();
        return;
      }

      // Priority 2: Header button (e.g. '✕' or close icon button in modal-header)
      const headerBtn = topModal.querySelector<HTMLElement>(
        '.modal-header button, [class*="modal-header"] button, .card-header button'
      );
      if (headerBtn) {
        headerBtn.click();
        return;
      }

      // Priority 3: Footer or cancel / dismiss button
      const allButtons = Array.from(topModal.querySelectorAll<HTMLElement>('button'));
      const cancelBtn = allButtons.find((btn) => {
        const text = (btn.textContent || '').trim().toLowerCase();
        return (
          text === 'cancel' ||
          text === 'close' ||
          text === 'iptal' ||
          text === 'kapat' ||
          text === 'dismiss' ||
          text === 'vazgeç' ||
          text === 'complete & close' ||
          btn.classList.contains('btn-secondary') ||
          btn.classList.contains('btn-cancel')
        );
      });
      if (cancelBtn) {
        cancelBtn.click();
        return;
      }

      // Priority 4: Click the backdrop itself if it has an attached click handler
      topModal.click();

      // Priority 5: Fallback handler
      if (onEscapeFallback) {
        onEscapeFallback();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onEscapeFallback]);
}
